/**
 * Terms of Service — BetUDontBet Poker / BUDB PKR
 */
import { LegalLayout } from "@/components/legal-layout";
import { BRAND } from "@shared/brand";

const UPDATED = "August 9, 2026";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" updated={UPDATED}>
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your access to and use
        of <strong>{BRAND.name}</strong> (<strong>{BRAND.shortName}</strong>) at{" "}
        {BRAND.origin} and related apps (the &quot;Service&quot;), provided by
        Grudge Studio (&quot;we&quot;, &quot;us&quot;). This page is also served
        at <a href="/tos">/tos</a>. By accessing or using the Service you agree
        to these Terms and our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>1. Eligibility</h2>
      <ul>
        <li>
          You must be at least <strong>18 years old</strong> (or the age of
          majority in your jurisdiction, if higher).
        </li>
        <li>
          You are responsible for complying with all laws that apply to you,
          including gambling, gaming, crypto-asset, and tax rules in your
          location. The Service may not be available where prohibited.
        </li>
        <li>
          You represent that you are not using the Service from a restricted
          jurisdiction where such use is unlawful.
        </li>
      </ul>

      <h2>2. Nature of the Service</h2>
      <p>
        {BRAND.name} offers multiplayer Texas Hold&apos;em tables, slots, and
        related features using <strong>GBUX</strong>, a Solana SPL token, and a
        server-side <strong>play ledger</strong> for table stakes. The Service
        is entertainment software operated on Cloudflare edge infrastructure
        with optional Grudge Studio fleet identity and wallets.
      </p>
      <ul>
        <li>
          Play chips in-game are <strong>play-ledger GBUX</strong> credited only
          after verified on-chain transfers (or explicit admin operations). There
          grants <strong>200 play GBUX</strong> once on first wallet connect
          (Phantom or any supported Solana wallet). Additional play balance
          requires a verified on-chain transfer or purchase.
        </li>
        <li>
          House rake and game rules are configured server-side and may change
          with notice on the site or in-product.
        </li>
        <li>
          Blockchain transactions are irreversible once confirmed. Network fees
          (e.g. SOL) are your responsibility.
        </li>
      </ul>

      <h2>3. Accounts &amp; wallets</h2>
      <ul>
        <li>
          You connect a non-custodial wallet (including{" "}
          <strong>Phantom</strong> via Phantom Connect / browser extension, and
          other Solana injectors such as Solflare) or an approved Grudge fleet
          wallet. You are solely responsible for wallet security, seed phrases,
          and device access.
        </li>
        <li>
          We never ask for private keys or seed phrases. Anyone asking for them
          is not us.
        </li>
        <li>
          Wallet signatures authorize specific on-chain operations (e.g. GBUX /
          SOL / USDT transfers). Approving a signature is your responsibility;
          review amounts and destinations in your wallet UI.
        </li>
        <li>
          You must not share accounts, automate play in ways that harm fair
          tables, collude, or exploit bugs.
        </li>
      </ul>

      <h2>4. Deposits, purchases, withdrawals &amp; GBUX</h2>
      <ul>
        <li>
          <strong>Play ledger.</strong> Poker, blackjack, and slots debit only
          the server play ledger. On-chain balances are not table chips until
          credited after verification.
        </li>
        <li>
          <strong>GBUX deposit.</strong> Transfer real SPL GBUX to the published
          BUDBai agent address; we credit play <strong>1:1</strong> after
          on-chain verification of the transaction signature.
        </li>
        <li>
          <strong>SOL / USDT purchase.</strong> When you buy play GBUX with SOL
          or USDT, the payment settles on the agent wallet; play GBUX is
          credited from the verified amount at the published rate (minus
          disclosed fee). Payment assets remain with the agent treasury, not as
          your on-chain play chips.
        </li>
        <li>
          Unverified or spoofed receipts will not credit play balance. Network
          fees (SOL) are yours.
        </li>
        <li>
          <strong>Player transfers</strong> on the Service are play-ledger only
          (server wallet → server wallet by username). They are not on-chain SPL
          transfers between users.
        </li>
        <li>
          <strong>Cash-out / swap out</strong> of play GBUX is by support only
          (email <strong>games@grudgestudio.org</strong> or Account Help ticket),
          subject to fraud checks and available balance. There is no automatic
          play→chain send from the app. We may delay or refuse requests that
          appear illicit or erroneous.
        </li>
        <li>
          Token prices and liquidity fluctuate. We do not guarantee any USD value
          of GBUX. GBUX and play balances are <strong>not</strong> bank deposits,
          are not FDIC/SIPC insured, and may become worthless.
        </li>
      </ul>

      <h2>5. Fair play &amp; prohibited conduct</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Cheat, collude, use bots or multi-accounting to gain unfair edge.</li>
        <li>Attack, scrape, or reverse-engineer the Service for abuse.</li>
        <li>Launder funds, finance crime, or use stolen wallets/assets.</li>
        <li>Harass other players or abuse chat/support channels.</li>
        <li>Circumvent geo, age, or security controls.</li>
      </ul>
      <p>
        We may suspend seats, freeze play balances pending review, void
        obviously corrupted hands, and ban wallets for violations.
      </p>

      <h2>6. House edge, slots &amp; variance</h2>
      <p>
        Poker involves skill and chance. Slots and similar products are
        chance-based with a house edge. Outcomes can include total loss of
        staked GBUX. Past results do not predict future results. Play only what
        you can afford to lose.
      </p>

      <h2>7. Responsible play</h2>
      <p>
        If gambling causes you harm, stop and seek help from local responsible
        gaming resources. We may impose limits or self-exclusion tools as they
        become available. This is not a substitute for professional help.
      </p>

      <h2>8. Intellectual property</h2>
      <p>
        The Service, branding ({BRAND.name}, {BRAND.shortName}, Grudge Studio),
        UI, art, and software are owned by us or our licensors. You receive a
        limited, revocable license to use the Service for personal,
        non-commercial play. You may not copy, resell, or rebrand the Service.
      </p>

      <h2>9. Third-party services</h2>
      <p>
        The Service integrates third parties including{" "}
        <strong>Phantom</strong> (wallet connection and signing), Solana network
        / RPC providers, Grudge ID SSO, Grudge Wallet / fleet APIs, and optional
        agent custody tooling. Each has its own terms and privacy policy. We are
        not responsible for third-party outages, hacks, or policy changes. On-chain
        activity is public by design.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED <strong>&quot;AS IS&quot;</strong> AND{" "}
        <strong>&quot;AS AVAILABLE&quot;</strong> WITHOUT WARRANTIES OF ANY KIND,
        EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT. We do not warrant uninterrupted or
        error-free operation, or that smart contracts / ledgers are free of
        bugs.
      </p>
      <p>
        Nothing on the Service is financial, legal, or tax advice. Digital
        assets are volatile and high risk.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE AND OUR AFFILIATES ARE NOT
        LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
        DAMAGES, OR FOR LOST PROFITS, LOST DATA, OR LOST GBUX VALUE, ARISING
        FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM RELATED
        TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE FEES YOU PAID US FOR
        THE SERVICE IN THE 3 MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S.
        DOLLARS (US$100).
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You will indemnify and hold harmless Grudge Studio and its operators
        from claims arising out of your misuse of the Service, violation of
        these Terms, or violation of law.
      </p>

      <h2>13. Suspension &amp; termination</h2>
      <p>
        We may suspend or terminate access at any time for risk, legal, or
        operational reasons. You may stop using the Service at any time.
        Provisions that should survive (including IP, disclaimers, liability
        limits) will survive termination.
      </p>

      <h2>14. Changes to the Service or Terms</h2>
      <p>
        We may modify features, stakes, rake, supported assets, or these Terms.
        Continued use after the updated date constitutes acceptance. If you
        disagree, stop using the Service.
      </p>

      <h2>15. Governing law</h2>
      <p>
        These Terms are governed by the laws of the State of Wyoming, USA,
        excluding conflict-of-law rules, unless mandatory consumer protections
        in your residence require otherwise. Courts in a venue we designate may
        hear disputes, subject to applicable law.
      </p>

      <h2>16. Contact</h2>
      <p>
        Legal: <a href="mailto:legal@grudge-studio.com">legal@grudge-studio.com</a>
        <br />
        Service: <a href={BRAND.origin}>{BRAND.origin}</a>
      </p>

      <p style={{ marginTop: "1.5rem", opacity: 0.75, fontSize: 14 }}>
        By playing on {BRAND.shortName} you acknowledge you have read these
        Terms and the Privacy Policy, and accept the risks of digital-asset
        gameplay.
      </p>
    </LegalLayout>
  );
}
